<template>
    <!--
        UI-35: pixel-perfect copy of the production tourist-site footer
        (zsh-frontend src/components/footer/Footer.tsx + AboutUsSection /
        LogoSection / HotelierSection). Same markup, links and design tokens.
        Root uses `.zsh-footer-prod` (not the legacy `.zsh-footer-wrapper`) so the
        global custom.scss `height: var(--footer-height)` clip does not apply and the
        footer can size to its content like the tourist site.
    -->
    <footer v-show="visible" v-intersect="onIntersect" class="zsh-footer-prod">
        <div class="top-footer">
            <!-- About us section -->
            <div class="about-us-section">
                <div class="section-title">{{ $t('footer.aboutUsSectionTitle') }}</div>
                <div class="links">
                    <div class="links-column">
                        <nuxt-link
                            v-for="(item, i) in aboutLinksCol1"
                            :key="`a1-${i}`"
                            class="footer-link"
                            :to="item.to"
                            :target="item.newTab ? '_blank' : undefined"
                            :rel="item.newTab ? 'noopener noreferrer' : undefined"
                            >{{ item.title }}</nuxt-link
                        >
                    </div>
                    <div class="links-column">
                        <nuxt-link
                            v-for="(item, i) in aboutLinksCol2"
                            :key="`a2-${i}`"
                            class="footer-link"
                            :to="item.to"
                            :target="item.newTab ? '_blank' : undefined"
                            :rel="item.newTab ? 'noopener noreferrer' : undefined"
                            >{{ item.title }}</nuxt-link
                        >
                    </div>
                </div>
                <div class="social-networks">
                    <div>{{ $t('footer.followUs') }}</div>
                    <nuxt-link
                        v-for="(icon, i) in socialLinks"
                        :key="`soc-${i}`"
                        :to="icon.href"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img :src="icon.src" :alt="icon.alt" class="social-icon" />
                    </nuxt-link>
                </div>
            </div>

            <!-- Logo section -->
            <div class="logo-section">
                <img src="/logo-footer.svg" alt="Zero Stars Hotels" class="logo" />
                <nuxt-link :to="extranetLoginTo" class="extranet-login-btn">
                    <v-icon icon="mdi-lock" size="16" />
                    {{ $t('footer.extranetLogin') }}
                </nuxt-link>
            </div>

            <!-- Hotelier section -->
            <div class="hotelier-section">
                <div class="section-title">{{ $t('footer.hotelierSectionTitle') }}</div>
                <div class="links">
                    <div class="links-column">
                        <nuxt-link
                            v-for="(item, i) in hotelierLinksCol1"
                            :key="`h1-${i}`"
                            class="footer-link"
                            :to="item.to"
                            >{{ item.title }}</nuxt-link
                        >
                    </div>
                    <div class="links-column">
                        <nuxt-link
                            v-for="(item, i) in hotelierLinksCol2"
                            :key="`h2-${i}`"
                            class="footer-link"
                            :to="item.to"
                            >{{ item.title }}</nuxt-link
                        >
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom footer -->
        <div class="bottom-footer">
            <div class="bottom-links">
                <nuxt-link
                    v-for="(item, i) in bottomLinks"
                    :key="`b-${i}`"
                    class="footer-link"
                    :to="item.to"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ item.title }}</nuxt-link
                >
            </div>
            <div class="copyright-notice">{{ $t('footer.copyrightNotice') }}</div>
        </div>
    </footer>
</template>

<script lang="ts">
    import {Component} from 'vue-facing-decorator'
    import {FooterBar as _FooterBar} from '@/components/FooterBar.vue'

    // UI-35: production origin for footer links that are relative on the tourist site.
    const ZSH_PROD_ORIGIN = 'https://www.zerostarshotels.com'

    @Component
    export class FooterBar extends _FooterBar {
        // About us — column 1 (partner site)
        get aboutLinksCol1() {
            return [
                {title: this.$t('footer.about'), to: 'https://partner.zerostarshotels.com/aboutus'},
                {title: this.$t('footer.ourVision'), to: 'https://partner.zerostarshotels.com/ourvision'},
                {title: this.$t('footer.zeroRoom'), to: 'https://partner.zerostarshotels.com/zero-room'},
                {
                    title: this.$t('footer.sustainableRoom'),
                    to: 'https://partner.zerostarshotels.com/sustainable-room',
                },
            ]
        }

        // About us — column 2
        get aboutLinksCol2() {
            return [
                {title: this.$t('footer.ecoElite'), to: `${ZSH_PROD_ORIGIN}/eco-elite`},
                {title: this.$t('footer.pressCenter'), to: 'https://partner.zerostarshotels.com/press-center'},
                {
                    title: this.$t('footer.careers'),
                    to: 'https://www.linkedin.com/company/zerostarshotels/',
                    newTab: true,
                },
                {title: this.$t('footer.contactUs'), to: `${ZSH_PROD_ORIGIN}/support-faq`},
            ]
        }

        // For Hotelier — column 1
        get hotelierLinksCol1() {
            return [
                {
                    title: this.$t('footer.increaseProfit'),
                    to: 'https://partner.zerostarshotels.com/increase-profit/',
                },
                {
                    title: this.$t('footer.increaseOccupancy'),
                    to: 'https://partner.zerostarshotels.com/increase-occupancy/',
                },
                {
                    title: this.$t('footer.sustainability'),
                    to: 'https://partner.zerostarshotels.com/sustainability/',
                },
                {title: this.$t('footer.faq'), to: `${ZSH_PROD_ORIGIN}/support-faq`},
            ]
        }

        // For Hotelier — column 2
        get hotelierLinksCol2() {
            return [
                {
                    title: this.$t('footer.customizationFlexibility'),
                    to: 'https://partner.zerostarshotels.com/customization-flexibility/',
                },
                {
                    title: this.$t('footer.brandingPromotion'),
                    to: 'https://partner.zerostarshotels.com/branding-promotion/',
                },
                {
                    title: this.$t('footer.employeeOperationalEfficiency'),
                    to: 'https://partner.zerostarshotels.com/employee-operational-efficiency/',
                },
                {
                    title: this.$t('footer.becomeAPartner'),
                    to: 'https://partner.zerostarshotels.com/become-a-partner/',
                },
            ]
        }

        // Bottom legal links (open in new tab, like the tourist site)
        get bottomLinks() {
            return [
                {title: this.$t('footer.termsAndConditions'), to: `${ZSH_PROD_ORIGIN}/terms-and-conditions`},
                {title: this.$t('footer.privacyStatement'), to: `${ZSH_PROD_ORIGIN}/privacy-policy`},
                {title: this.$t('footer.cookiesNotice'), to: `${ZSH_PROD_ORIGIN}/cookies-notice`},
                {title: this.$t('footer.cookieStatement'), to: `${ZSH_PROD_ORIGIN}/cookies-statement`},
            ]
        }

        get socialLinks() {
            return [
                {src: '/facebook.svg', alt: 'facebook', href: 'https://www.facebook.com/zerostarshotels/'},
                {src: '/instagram.svg', alt: 'instagram', href: 'https://www.instagram.com/zerostarshotels/'},
                {src: '/linkedin.svg', alt: 'linkedin', href: 'https://www.linkedin.com/company/zerostarshotels/'},
            ]
        }

        // "Extranet login" CTA: inside the Extranet this maps to its own b2b-auth entry.
        get extranetLoginTo() {
            return {name: 'b2b-auth'}
        }
    }

    export default FooterBar
</script>

<style scoped lang="scss">
    // Design tokens mirrored from zsh-frontend (src/styles/_colors.scss / _variables.scss / _mixins.scss):
    //   $color-primary-bg-000 #ffffff | -400 #51b5bf | -600 #2d7784
    //   gap(n) = n*0.5rem | $font-size-large 20px | $font-size-regular 16px | desktop = min-width:1025px
    $desktop: 1025px;

    .zsh-footer-prod {
        color: #ffffff;
        background: #2d7784;
        padding: 40px 20px; // gap(5) gap(2.5)
        width: 100%;
        box-sizing: border-box;

        @media (min-width: $desktop) {
            background-image: url('/footer/left-top-bg.svg'), url('/footer/right-bottom-bg.svg');
            background-position:
                left top,
                right bottom;
            background-repeat: no-repeat, no-repeat;
            background-color: #2d7784;
            background-size:
                auto 80%,
                auto 65%;
            padding: 0 0 44px; // gap(5.5)
        }

        .top-footer {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            margin-bottom: 32px; // gap(4)
            max-width: 1360px; // $base-content-width (AdaptiveContentWrapper)
            margin-left: auto;
            margin-right: auto;

            @media (min-width: $desktop) {
                flex-direction: row;
                margin-bottom: 184px; // gap(23)
            }
        }

        // Shared footer link (FooterLink.module.scss)
        .footer-link {
            text-decoration: none;
            color: #ffffff;
            position: relative;
            display: inline-block;
            transition: 0.5s;
            opacity: 0.8;
            margin-bottom: 8px; // gap(1)

            &::after {
                content: '';
                position: absolute;
                left: 0;
                bottom: -2px;
                height: 2px;
                width: 0;
                background-color: #51b5bf; // $color-primary-bg-400
                transition: 0.5s;
            }

            &:hover,
            &:active {
                opacity: 1;

                &::after {
                    width: 100%;
                }
            }
        }

        .section-title {
            font-size: 20px; // $font-size-large
            margin-bottom: 16px; // gap(2)
        }

        .links {
            display: flex;
            flex-direction: column;

            @media (min-width: $desktop) {
                flex-direction: row;
            }

            .links-column {
                display: flex;
                flex-direction: column;
                margin-bottom: 8px; // gap(1)
                line-height: 160%;

                &:first-child {
                    @media (min-width: $desktop) {
                        margin-right: 32px; // gap(4)
                    }
                }
            }
        }

        .about-us-section {
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-top: 0;

            @media (min-width: $desktop) {
                margin-top: 106px; // gap(13.25)
                width: 29%;
            }

            .social-networks {
                display: flex;
                padding-top: 16px; // gap(2)
                border-top: 1px solid rgba(255, 255, 255, 0.4); // rgba($color-primary-bg-000, 0.4)
                text-transform: uppercase;
                font-size: 16px; // $font-size-regular
                align-items: center;
                gap: 16px; // gap(2)

                a {
                    display: inline-flex;
                    height: 30px; // to-rem(30px)
                }

                .social-icon {
                    height: 30px;
                    width: 30px;
                    opacity: 0.8;
                    transition: 0.4s ease;
                    cursor: pointer;
                }

                a:hover .social-icon {
                    opacity: 1;
                }
            }
        }

        .logo-section {
            overflow: hidden;
            display: flex;
            align-items: center;
            flex-direction: column;
            width: 100%;
            margin-top: 0;
            margin-bottom: 16px; // gap(2)

            @media (min-width: $desktop) {
                width: 31%;
                margin-top: 32px; // gap(2)
                margin-bottom: 0;
            }

            .logo {
                width: 285px; // to-rem(285px) mobile
                max-width: 100%;

                @media (min-width: $desktop) {
                    width: 100%;
                }
            }

            .extranet-login-btn {
                color: #ffffff;
                text-decoration: none;
                display: inline-flex;
                align-items: center;
                gap: 8px;
                margin-top: 16px; // gap(2)
                // btn-secondary (zsh-frontend globals.scss): 1px white border + antd radius/padding
                border: 1px solid #ffffff;
                border-radius: 6px;
                padding: 4px 15px;
                transition:
                    color 0.3s,
                    border-color 0.3s;

                &:hover {
                    color: #daf2f3; // $color-primary-bg-100
                    border-color: #daf2f3;
                }

                .v-icon {
                    opacity: inherit;
                }
            }
        }

        .hotelier-section {
            display: flex;
            flex-direction: column;
            width: 100%;
            margin-top: 0;

            @media (min-width: $desktop) {
                margin-top: 106px; // gap(13.25)
                width: 25%;
            }
        }

        .bottom-footer {
            display: flex;
            flex-direction: column;
            max-width: 1360px;
            margin-left: auto;
            margin-right: auto;

            @media (min-width: $desktop) {
                flex-direction: row;
                justify-content: space-between;
            }

            .bottom-links {
                display: flex;
                flex-direction: column;
                gap: 24px; // gap(3)

                @media (min-width: $desktop) {
                    flex-direction: row;
                }
            }

            .copyright-notice {
                margin-top: 24px; // gap(3)

                @media (min-width: $desktop) {
                    margin-top: 0;
                }
            }
        }
    }
</style>
